import { Fragment, React, PropTypes, css, styled } from '../lib/imports';

import { ColorProvider, HeatStreakText, Spacing, Text, LeaderboardResultsDivider } from '../components';
import { useCdnImage, getAssetSet } from '../app/context/config';
import Loader from '../ui/Loader';
import { useSelector } from 'react-redux';
import Image from '../components/chrome/Image';

const assetSet = (filename) => getAssetSet(useCdnImage, filename);

const tableStyles = ({ theme }) => css`
  table-layout: fixed;
  width: 100%;
  tbody {
    position: relative;
  }
  tbody tr:nth-of-type(odd) {
    background-color: #f2f2f2;
  }
  tbody tr.active {
    background-color: ${theme.colors.g100};
  }
  tbody td.devider {
    padding: 0;
    overflow: visible;
  }
  tbody tr.deviderRow {
    height: 90px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: absolute;
    width: 100%;
  }
  border-collapse: collapse;

  thead th:nth-of-type(1) {
    width: 17.5%;
  }

  thead th:nth-of-type(2) {
    width: 30%;
  }

  thead th:nth-of-type(3) {
    width: 17.5%;
  }

  thead th:nth-of-type(4) {
    width: 17.5%;
  }

  thead th:nth-of-type(5) {
    width: 17.5%;
  }

  td {
    padding: 0.9em 0.3em 0.9em 0.3em;
    font-size: 15px;
    text-overflow: ellipsis;
    overflow: hidden;
  }
  th {
    padding: 0.9em 0.3em 0.9em 0.3em;
    word-break: break-word;
    font-size: 13px;
    letter-spacing: -0.24px;
  }
`;
const Table = styled('table')`
  ${tableStyles}
`;

const theadStyles = () => css`
  transform: scale(1);
  box-shadow: 0 4px 6px -4px rgba(0, 0, 0, 0.25);
  color: #07345c;
`;
const THead = styled('thead')`
  ${theadStyles}
`;

const cellStyles = () => css`
  text-align: center;
  vertical-align: middle;
`;

const Cell = styled('td')`
  ${cellStyles}
  text-align: ${(props) => props.textAlign}
`;

const THLeftAlign = styled('th')`
  text-align: left;
`;

const rankStyles = () => css`
  width: 1.8em;
  height: 1.8em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-image: url('https://cdn.rbi.digital/hockey/icon_badge_gold.svg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  padding-top: 2px;
`;

const Rank = styled('span')`
  ${rankStyles}
`;

const Caption = styled('caption')`
  display: none;
`;

const FillContainer = styled('div')`
  display: flex;
  flex-direction: column;
  background-color: #f2f2f2;
  height: 70vh;
  justify-content: center;
  align-items: center;
`;
const TopFade = styled('div')`
  height: 100px;
  background: linear-gradient(0deg, rgba(255, 255, 255, 1) 30%, rgba(255, 255, 255, 0) 100%);
`;
const BottomFade = styled('div')`
  height: 100px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 1) 30%, rgba(255, 255, 255, 0) 100%);
`;

const LeaderboardTable = React.forwardRef(({ data, options, selectedView }, ref) => {
  const { strings, leaderboard, config } = useSelector((state) => state);

  const { contestants, isTopRanked, rank, totalUsers } = data;

  const { language } = config;
  const { isLoading, hasUserStats } = leaderboard;

  if (!isLoading && hasUserStats && contestants) {
    const numLowerRankedPlayers = totalUsers - rank;

    const isLoading = leaderboard.isLoading;
    const challengeErrorText = leaderboard.challengeErrorCode && strings[leaderboard.challengeErrorCode];

    const assetSrc = assetSet(['loadingSticks']);

    const view = options.filter((opt) => opt.key === selectedView);

    const rankText = strings['leaderboard/table/rank'];
    const playerText = strings['leaderboard/table/player'];
    const correctText = strings['leaderboard/table/correct'];
    const pickText = strings['leaderboard/table/pick'];
    const streakText = strings['leaderboard/table/streak'];
    const numLowerRankedPlayersText = strings['leaderboard/table/endOfPlayerResults'];

    const buildListCells = ({ key, rank, username, correctPicks, pickAccuracy, scoringStreak }) => {
      return (
        <>
          <Cell key={`${key}_${rank}`}>
            {rank < 6 ? <Rank>{rank}</Rank> : Number(rank).toLocaleString(`${language || 'en'}-CA`)}
          </Cell>
          <Cell textAlign='left'>{username}</Cell>
          <Cell>{correctPicks}</Cell>
          <Cell>{pickAccuracy}%</Cell>
          <Cell>
            <HeatStreakText variant={scoringStreak > 0 ? 'hot' : 'cold'} showIcon size='giga' text={scoringStreak} />
          </Cell>
        </>
      );
    };

    const buildListElm = ({ selectedView, user, dataSection, playerIndex, currentContestant }) => {
      if (currentContestant) {
        return (
          <tr
            key={`${selectedView}_${dataSection}_${playerIndex}`}
            className={currentContestant ? 'active' : ''}
            ref={ref}
          >
            {buildListCells({
              key: `${selectedView}_${dataSection}_${playerIndex}`,
              rank: user.rank,
              username: user.username,
              correctPicks: user.correctPicks,
              pickAccuracy: user.pickAccuracy,
              scoringStreak: user.scoringStreak,
            })}
          </tr>
        );
      }
      return (
        <tr
          key={`${selectedView}_${dataSection}_${playerIndex}`}
          className={currentContestant ? 'current' : ''}
          ref={ref}
        >
          {buildListCells({
            key: `${selectedView}_${dataSection}_${playerIndex}`,
            rank: user.rank,
            username: user.username,
            correctPicks: user.correctPicks,
            pickAccuracy: user.pickAccuracy,
            scoringStreak: user.scoringStreak,
          })}
        </tr>
      );
    };
    return (
      <>
        {(isLoading || contestants) && (
          <Table>
            {view.length > 0 && <Caption>{view[0].value}</Caption>}
            <THead>
              <tr>
                <th>{rankText}</th>
                <THLeftAlign>{playerText}</THLeftAlign>
                <th>{correctText}</th>
                <th>{pickText}</th>
                <th>{streakText}</th>
              </tr>
            </THead>
            <tbody>
              {!isLoading &&
                hasUserStats &&
                contestants &&
                contestants.map((set, i) => {
                  return set.map((user, j) => {
                    const currentContestantFlag = user.me === true;
                    // render an add space after the 4th player
                    if (i === 0 && j === 4) {
                      return (
                        <Fragment key={`row_${selectedView}_ad_${i}_${j}`}>
                          {buildListElm({
                            selectedView,
                            user,
                            dataSection: i,
                            playerIndex: j,
                            currentContestant: currentContestantFlag,
                          })}
                        </Fragment>
                      );
                    }

                    // render a divider between the two lists of users
                    if (
                      contestants[i + 1] !== undefined &&
                      contestants[i + 1].length !== 0 &&
                      set.length - 1 === j &&
                      !isTopRanked
                    ) {
                      return (
                        <>
                          {buildListElm({
                            selectedView,
                            user,
                            dataSection: i,
                            playerIndex: j,
                            currentContestant: currentContestantFlag,
                          })}
                          <tr key={`${selectedView}_${i}_spacer`} className='deviderRow'>
                            <Cell colSpan={5} className='devider'>
                              <TopFade />
                              <LeaderboardResultsDivider playerCount={rank - 1} />
                              <BottomFade />
                            </Cell>
                          </tr>
                        </>
                      );
                    }

                    // render the num of players that are ranked under current
                    if (contestants[i + 1] === undefined && set.length - 1 === j) {
                      return (
                        <Fragment key={`${selectedView}_${i}_players_below_container`}>
                          {buildListElm({
                            selectedView,
                            user,
                            dataSection: i,
                            playerIndex: j,
                            currentContestant: currentContestantFlag,
                          })}
                          {rank && (
                            <tr key={`${selectedView}_${i}_players_below`} className=''>
                              <Cell colSpan={5} className=''>
                                <Text as='span' size='tera' bold>
                                  {numLowerRankedPlayersText(numLowerRankedPlayers)}
                                </Text>
                              </Cell>
                            </tr>
                          )}
                        </Fragment>
                      );
                    }

                    return buildListElm({
                      selectedView,
                      user,
                      dataSection: i,
                      playerIndex: j,
                      currentContestant: currentContestantFlag,
                    });
                  });
                })}
            </tbody>
          </Table>
        )}
        {!isLoading && challengeErrorText && (
          <FillContainer style={{ height: '50vh' }}>
            <Image src={assetSrc} style={{ height: '64px', width: 'auto', margin: '0 0 16px 0' }} />
            <Spacing sizeLeft='zetta' sizeRight='zetta' sizeTop='giga'>
              <ColorProvider variant='black'>
                <Text as='span' size='giga' centered>
                  {challengeErrorText}
                </Text>
              </ColorProvider>
            </Spacing>
          </FillContainer>
        )}
      </>
    );
  }
  return (
    <FillContainer>
      <Loader isFixed={false} />
    </FillContainer>
  );
});

LeaderboardTable.propTypes = {
  isLoading: PropTypes.bool,
  data: PropTypes.arrayOf(PropTypes.array),
  leaderboardAdConfig: PropTypes.object,
  lid: PropTypes.string,
  platform: PropTypes.string,
  language: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.exact({ key: PropTypes.string, value: PropTypes.string })),
};

export default LeaderboardTable;
